export type Framework = {
	slug: string;
	name: string;
	description: string;
	metaDescription: string;
	icon: string;
	installCommand: string;
	codeExample: string;
	envExample: string;
	features: string[];
	relatedBlogPost?: string;
};

export type MigrationStep = {
	title: string;
	description: string;
	before?: string;
	after?: string;
	language?: string;
};

export type MigrationGuide = {
	slug: string;
	name: string;
	from: string;
	description: string;
	metaDescription: string;
	steps: MigrationStep[];
};

export const frameworks: Framework[] = [
	{
		slug: "nextjs",
		name: "Next.js",
		description: "Send email from Next.js using Server Actions and API routes",
		metaDescription:
			"Learn how to send email in Next.js using SendPigeon. Server Actions, API routes, templates, and React Email with full TypeScript examples.",
		icon: "nextjs",
		installCommand: "npm install sendpigeon",
		codeExample: `// app/actions/contact.ts
"use server";

import { SendPigeon } from "sendpigeon";

const pigeon = new SendPigeon(process.env.SENDPIGEON_API_KEY!);

export async function sendContactEmail(formData: FormData) {
  const email = formData.get("email") as string;
  const name = formData.get("name") as string;

  const { data, error } = await pigeon.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true, emailId: data.id };
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works with Server Actions and API routes",
			"Compatible with Node.js and Edge Runtime",
			"Deploy to Vercel, Netlify, or any host",
		],
		relatedBlogPost: "/blog/send-email-nextjs",
	},
	{
		slug: "remix",
		name: "Remix",
		description: "Send transactional emails from your Remix application",
		metaDescription:
			"Learn how to send emails from Remix using SendPigeon. Simple setup with action functions and environment variables.",
		icon: "remix",
		installCommand: "npm install sendpigeon",
		codeExample: `// app/routes/contact.tsx
import type { ActionFunctionArgs } from "@remix-run/node";
import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(process.env.SENDPIGEON_API_KEY!);

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const name = formData.get("name") as string;

  await client.emails.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  return { success: true };
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works in Remix action functions",
			"Full TypeScript support",
			"Deploy to Vercel, Fly.io, or any Node.js host",
		],
	},
	{
		slug: "astro",
		name: "Astro",
		description: "Send emails from Astro API routes and server endpoints",
		metaDescription:
			"Learn how to send emails from Astro using SendPigeon. Works with SSR and API endpoints.",
		icon: "astro",
		installCommand: "npm install sendpigeon",
		codeExample: `// src/pages/api/contact.ts
import type { APIRoute } from "astro";
import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(import.meta.env.SENDPIGEON_API_KEY);

export const POST: APIRoute = async ({ request }) => {
  const { email, name } = await request.json();

  await client.emails.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  return new Response(JSON.stringify({ success: true }), {
    status: 200,
    headers: { "Content-Type": "application/json" },
  });
};`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works with Astro SSR mode",
			"API routes and server endpoints",
			"Deploy anywhere Astro runs",
		],
	},
	{
		slug: "sveltekit",
		name: "SvelteKit",
		description: "Send emails from SvelteKit form actions and API routes",
		metaDescription:
			"Learn how to send emails from SvelteKit using SendPigeon. Works with form actions and +server.ts endpoints.",
		icon: "svelte",
		installCommand: "npm install sendpigeon",
		codeExample: `// src/routes/contact/+page.server.ts
import type { Actions } from "./$types";
import { SendPigeon } from "sendpigeon";
import { SENDPIGEON_API_KEY } from "$env/static/private";

const client = new SendPigeon(SENDPIGEON_API_KEY);

export const actions: Actions = {
  default: async ({ request }) => {
    const data = await request.formData();
    const email = data.get("email") as string;
    const name = data.get("name") as string;

    await client.emails.send({
      from: "hello@yourdomain.com",
      to: email,
      subject: \`Thanks for reaching out, \${name}!\`,
      html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
    });

    return { success: true };
  },
};`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works with SvelteKit form actions",
			"Server-side only (secure)",
			"Full TypeScript support",
		],
	},
	{
		slug: "nuxt",
		name: "Nuxt",
		description: "Send emails from Nuxt server routes and API endpoints",
		metaDescription:
			"Learn how to send emails from Nuxt 3 using SendPigeon. Works with server routes and Nitro.",
		icon: "nuxt",
		installCommand: "npm install sendpigeon",
		codeExample: `// server/api/contact.post.ts
import { SendPigeon } from "sendpigeon";

export default defineEventHandler(async (event) => {
  const config = useRuntimeConfig();
  const client = new SendPigeon(config.sendpigeonApiKey);
  const { email, name } = await readBody(event);

  await client.emails.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  return { success: true };
});`,
		envExample: `// nuxt.config.ts
export default defineNuxtConfig({
  runtimeConfig: {
    sendpigeonApiKey: process.env.SENDPIGEON_API_KEY,
  },
})`,
		features: [
			"Works with Nuxt 3 server routes",
			"Nitro server engine",
			"Auto-imports and TypeScript",
		],
	},
	{
		slug: "hono",
		name: "Hono",
		description: "Send emails from Hono on any runtime",
		metaDescription:
			"Learn how to send emails from Hono using SendPigeon. Works on Cloudflare Workers, Deno, Bun, and Node.js.",
		icon: "hono",
		installCommand: "npm install sendpigeon",
		codeExample: `import { Hono } from "hono";
import { SendPigeon } from "sendpigeon";

const app = new Hono();

app.post("/contact", async (c) => {
  const client = new SendPigeon(c.env.SENDPIGEON_API_KEY);
  const { email, name } = await c.req.json();

  await client.emails.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  return c.json({ success: true });
});

export default app;`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works on Cloudflare Workers",
			"Deno, Bun, Node.js support",
			"Ultrafast and lightweight",
		],
		relatedBlogPost: "send-email-hono",
	},
	{
		slug: "fastify",
		name: "Fastify",
		description: "Send emails from Fastify routes",
		metaDescription:
			"Learn how to send emails from Fastify using SendPigeon. Fast and low overhead email sending.",
		icon: "fastify",
		installCommand: "npm install sendpigeon",
		codeExample: `import Fastify from "fastify";
import { SendPigeon } from "sendpigeon";

const fastify = Fastify();
const client = new SendPigeon(process.env.SENDPIGEON_API_KEY!);

fastify.post("/contact", async (request, reply) => {
  const { email, name } = request.body as { email: string; name: string };

  await client.emails.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  return { success: true };
});

fastify.listen({ port: 3000 });`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"High performance",
			"Schema validation ready",
			"Plugin ecosystem",
		],
	},
	{
		slug: "koa",
		name: "Koa",
		description: "Send emails from Koa middleware and routes",
		metaDescription:
			"Learn how to send emails from Koa using SendPigeon. Elegant middleware-based email sending.",
		icon: "koa",
		installCommand: "npm install sendpigeon",
		codeExample: `import Koa from "koa";
import Router from "@koa/router";
import bodyParser from "koa-bodyparser";
import { SendPigeon } from "sendpigeon";

const app = new Koa();
const router = new Router();
const client = new SendPigeon(process.env.SENDPIGEON_API_KEY!);

router.post("/contact", async (ctx) => {
  const { email, name } = ctx.request.body as { email: string; name: string };

  await client.emails.send({
    from: "hello@yourdomain.com",
    to: email,
    subject: \`Thanks for reaching out, \${name}!\`,
    html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
  });

  ctx.body = { success: true };
});

app.use(bodyParser());
app.use(router.routes());
app.listen(3000);`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Async/await native",
			"Middleware-based",
			"Lightweight and modular",
		],
	},
	{
		slug: "deno",
		name: "Deno",
		description: "Send emails from Deno with zero config",
		metaDescription:
			"Learn how to send emails from Deno using SendPigeon. TypeScript-first with secure defaults.",
		icon: "deno",
		installCommand: "# No install needed - import from npm",
		codeExample: `import { SendPigeon } from "npm:sendpigeon";

const client = new SendPigeon(Deno.env.get("SENDPIGEON_API_KEY")!);

Deno.serve(async (req) => {
  if (req.method === "POST" && new URL(req.url).pathname === "/contact") {
    const { email, name } = await req.json();

    await client.emails.send({
      from: "hello@yourdomain.com",
      to: email,
      subject: \`Thanks for reaching out, \${name}!\`,
      html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
    });

    return Response.json({ success: true });
  }

  return new Response("Not found", { status: 404 });
});`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"TypeScript out of the box",
			"Secure by default",
			"npm compatibility",
		],
	},
	{
		slug: "bun",
		name: "Bun",
		description: "Send emails from Bun with blazing speed",
		metaDescription:
			"Learn how to send emails from Bun using SendPigeon. Fast runtime, fast emails.",
		icon: "bun",
		installCommand: "bun add sendpigeon",
		codeExample: `import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(Bun.env.SENDPIGEON_API_KEY!);

Bun.serve({
  port: 3000,
  async fetch(req) {
    if (req.method === "POST" && new URL(req.url).pathname === "/contact") {
      const { email, name } = await req.json();

      await client.emails.send({
        from: "hello@yourdomain.com",
        to: email,
        subject: \`Thanks for reaching out, \${name}!\`,
        html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
      });

      return Response.json({ success: true });
    }

    return new Response("Not found", { status: 404 });
  },
});`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Fastest JavaScript runtime",
			"Drop-in Node.js replacement",
			"Built-in TypeScript",
		],
	},
	{
		slug: "elysia",
		name: "Elysia",
		description: "Send emails from Elysia with end-to-end type safety",
		metaDescription:
			"Learn how to send emails from Elysia using SendPigeon. Type-safe and performant on Bun.",
		icon: "elysia",
		installCommand: "bun add sendpigeon",
		codeExample: `import { Elysia, t } from "elysia";
import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(Bun.env.SENDPIGEON_API_KEY!);

const app = new Elysia()
  .post(
    "/contact",
    async ({ body }) => {
      await client.emails.send({
        from: "hello@yourdomain.com",
        to: body.email,
        subject: \`Thanks for reaching out, \${body.name}!\`,
        html: \`<h1>We got your message</h1><p>We'll get back to you soon.</p>\`,
      });

      return { success: true };
    },
    {
      body: t.Object({
        email: t.String({ format: "email" }),
        name: t.String(),
      }),
    }
  )
  .listen(3000);`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: ["End-to-end type safety", "Built-in validation", "Runs on Bun"],
	},
	{
		slug: "django",
		name: "Django",
		description: "Send emails from Django views and management commands",
		metaDescription:
			"Learn how to send emails from Django using SendPigeon Python SDK. Simple integration with views and async support.",
		icon: "django",
		installCommand: "pip install sendpigeon",
		codeExample: `# views.py
import json
import os

from django.http import JsonResponse
from django.views.decorators.http import require_POST
from sendpigeon import SendPigeon

client = SendPigeon(os.environ["SENDPIGEON_API_KEY"])

@require_POST
def contact(request):
    data = json.loads(request.body)

    client.send(
        to=data["email"],
        from_email="hello@yourdomain.com",
        subject=f"Thanks for reaching out, {data['name']}!",
        html="<h1>We got your message</h1><p>We'll get back to you soon.</p>",
    )

    return JsonResponse({"success": True})`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works with Django views",
			"Async support with Django 4.1+",
			"Management command integration",
		],
	},
	{
		slug: "flask",
		name: "Flask",
		description: "Send emails from Flask routes and blueprints",
		metaDescription:
			"Learn how to send emails from Flask using SendPigeon Python SDK. Lightweight integration for any Flask app.",
		icon: "flask",
		installCommand: "pip install sendpigeon",
		codeExample: `import os
from flask import Flask, request, jsonify
from sendpigeon import SendPigeon

app = Flask(__name__)
client = SendPigeon(os.environ["SENDPIGEON_API_KEY"])

@app.route("/contact", methods=["POST"])
def contact():
    data = request.get_json()

    client.send(
        to=data["email"],
        from_email="hello@yourdomain.com",
        subject=f"Thanks for reaching out, {data['name']}!",
        html="<h1>We got your message</h1><p>We'll get back to you soon.</p>",
    )

    return jsonify({"success": True})

if __name__ == "__main__":
    app.run()`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Simple Flask integration",
			"Works with blueprints",
			"Deploy anywhere Python runs",
		],
	},
	{
		slug: "fastapi",
		name: "FastAPI",
		description: "Send emails from FastAPI with async support",
		metaDescription:
			"Learn how to send emails from FastAPI using SendPigeon Python SDK. Async-native with Pydantic validation.",
		icon: "fastapi",
		installCommand: "pip install sendpigeon",
		codeExample: `import os
from fastapi import FastAPI
from pydantic import BaseModel, EmailStr
from sendpigeon import SendPigeon

app = FastAPI()
client = SendPigeon(os.environ["SENDPIGEON_API_KEY"])

class ContactRequest(BaseModel):
    email: EmailStr
    name: str

@app.post("/contact")
async def contact(data: ContactRequest):
    client.send(
        to=data.email,
        from_email="hello@yourdomain.com",
        subject=f"Thanks for reaching out, {data.name}!",
        html="<h1>We got your message</h1><p>We'll get back to you soon.</p>",
    )

    return {"success": True}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Async-native integration",
			"Pydantic validation",
			"OpenAPI docs included",
		],
	},
	{
		slug: "gin",
		name: "Gin",
		description: "Send emails from Gin HTTP handlers",
		metaDescription:
			"Learn how to send emails from Gin using SendPigeon Go SDK. Fast and efficient email sending.",
		icon: "go",
		installCommand: "go get github.com/sendpigeon/sendpigeon-go",
		codeExample: `package main

import (
	"net/http"
	"os"

	"github.com/gin-gonic/gin"
	"github.com/sendpigeon/sendpigeon-go/sendpigeon"
)

func main() {
	client := sendpigeon.New(os.Getenv("SENDPIGEON_API_KEY"))
	r := gin.Default()

	r.POST("/contact", func(c *gin.Context) {
		var req struct {
			Email string \`json:"email"\`
			Name  string \`json:"name"\`
		}
		c.BindJSON(&req)

		client.Send(c.Request.Context(), sendpigeon.SendEmailRequest{
			To:      []string{req.Email},
			From:    "hello@yourdomain.com",
			Subject: "Thanks for reaching out, " + req.Name + "!",
			HTML:    "<h1>We got your message</h1><p>We'll get back to you soon.</p>",
		})

		c.JSON(http.StatusOK, gin.H{"success": true})
	})

	r.Run(":3000")
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Context-aware requests",
			"High performance",
			"Middleware support",
		],
	},
	{
		slug: "echo",
		name: "Echo",
		description: "Send emails from Echo HTTP handlers",
		metaDescription:
			"Learn how to send emails from Echo using SendPigeon Go SDK. Minimalist and extensible.",
		icon: "go",
		installCommand: "go get github.com/sendpigeon/sendpigeon-go",
		codeExample: `package main

import (
	"net/http"
	"os"

	"github.com/labstack/echo/v4"
	"github.com/sendpigeon/sendpigeon-go/sendpigeon"
)

func main() {
	client := sendpigeon.New(os.Getenv("SENDPIGEON_API_KEY"))
	e := echo.New()

	e.POST("/contact", func(c echo.Context) error {
		var req struct {
			Email string \`json:"email"\`
			Name  string \`json:"name"\`
		}
		c.Bind(&req)

		client.Send(c.Request().Context(), sendpigeon.SendEmailRequest{
			To:      []string{req.Email},
			From:    "hello@yourdomain.com",
			Subject: "Thanks for reaching out, " + req.Name + "!",
			HTML:    "<h1>We got your message</h1><p>We'll get back to you soon.</p>",
		})

		return c.JSON(http.StatusOK, map[string]bool{"success": true})
	})

	e.Start(":3000")
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Minimalist design",
			"Context propagation",
			"Extensible middleware",
		],
	},
	{
		slug: "fiber",
		name: "Fiber",
		description: "Send emails from Fiber with Express-like syntax",
		metaDescription:
			"Learn how to send emails from Fiber using SendPigeon Go SDK. Express-inspired and blazing fast.",
		icon: "go",
		installCommand: "go get github.com/sendpigeon/sendpigeon-go",
		codeExample: `package main

import (
	"os"

	"github.com/gofiber/fiber/v2"
	"github.com/sendpigeon/sendpigeon-go/sendpigeon"
)

func main() {
	client := sendpigeon.New(os.Getenv("SENDPIGEON_API_KEY"))
	app := fiber.New()

	app.Post("/contact", func(c *fiber.Ctx) error {
		var req struct {
			Email string \`json:"email"\`
			Name  string \`json:"name"\`
		}
		c.BodyParser(&req)

		client.Send(c.UserContext(), sendpigeon.SendEmailRequest{
			To:      []string{req.Email},
			From:    "hello@yourdomain.com",
			Subject: "Thanks for reaching out, " + req.Name + "!",
			HTML:    "<h1>We got your message</h1><p>We'll get back to you soon.</p>",
		})

		return c.JSON(fiber.Map{"success": true})
	})

	app.Listen(":3000")
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Express-like syntax",
			"Zero memory allocation",
			"Fasthttp powered",
		],
	},
	{
		slug: "laravel",
		name: "Laravel",
		description: "Send emails from Laravel controllers and jobs",
		metaDescription:
			"Learn how to send emails from Laravel using SendPigeon PHP SDK. Clean integration with queues and notifications.",
		icon: "laravel",
		installCommand: "composer require sendpigeon/sendpigeon",
		codeExample: `<?php
// app/Http/Controllers/ContactController.php
namespace App\\Http\\Controllers;

use Illuminate\\Http\\Request;
use SendPigeon\\SendPigeon;

class ContactController extends Controller
{
    public function store(Request $request)
    {
        $validated = $request->validate([
            'email' => 'required|email',
            'name' => 'required|string',
        ]);

        $client = new SendPigeon(config('services.sendpigeon.key'));

        $client->send(
            to: $validated['email'],
            from: 'hello@yourdomain.com',
            subject: "Thanks for reaching out, {$validated['name']}!",
            html: '<h1>We got your message</h1><p>We\\'ll get back to you soon.</p>',
        );

        return response()->json(['success' => true]);
    }
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Works with Laravel queues",
			"Notification channel support",
			"Blade template integration",
		],
	},
	{
		slug: "symfony",
		name: "Symfony",
		description: "Send emails from Symfony controllers and services",
		metaDescription:
			"Learn how to send emails from Symfony using SendPigeon PHP SDK. Service container and Messenger integration.",
		icon: "symfony",
		installCommand: "composer require sendpigeon/sendpigeon",
		codeExample: `<?php
// src/Controller/ContactController.php
namespace App\\Controller;

use SendPigeon\\SendPigeon;
use Symfony\\Bundle\\FrameworkBundle\\Controller\\AbstractController;
use Symfony\\Component\\HttpFoundation\\JsonResponse;
use Symfony\\Component\\HttpFoundation\\Request;
use Symfony\\Component\\Routing\\Annotation\\Route;

class ContactController extends AbstractController
{
    #[Route('/contact', methods: ['POST'])]
    public function contact(Request $request): JsonResponse
    {
        $data = json_decode($request->getContent(), true);

        $client = new SendPigeon($_ENV['SENDPIGEON_API_KEY']);

        $client->send(
            to: $data['email'],
            from: 'hello@yourdomain.com',
            subject: "Thanks for reaching out, {$data['name']}!",
            html: '<h1>We got your message</h1><p>We\\'ll get back to you soon.</p>',
        );

        return $this->json(['success' => true]);
    }
}`,
		envExample: "SENDPIGEON_API_KEY=your_api_key_here",
		features: [
			"Service container integration",
			"Messenger async support",
			"Twig template support",
		],
	},
];

export function getAllFrameworks(): Framework[] {
	return frameworks;
}

export function getFrameworkBySlug(slug: string): Framework | undefined {
	return frameworks.find((f) => f.slug === slug);
}

export const migrations: MigrationGuide[] = [
	{
		slug: "from-resend",
		name: "Resend",
		from: "Resend",
		description: "Switch from Resend to SendPigeon in 5 minutes",
		metaDescription:
			"Step-by-step guide to migrate from Resend to SendPigeon with code examples.",
		steps: [
			{
				title: "Update your API key",
				description:
					"Replace your Resend API key with SendPigeon. Both use environment variables.",
				before: "RESEND_API_KEY=re_123abc...",
				after: "SENDPIGEON_API_KEY=sk_live_abc123...",
				language: "bash",
			},
			{
				title: "Update imports",
				description: "Change the import statement to use SendPigeon SDK.",
				before: `import { Resend } from "resend";

const resend = new Resend(process.env.RESEND_API_KEY);`,
				after: `import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(process.env.SENDPIGEON_API_KEY);`,
				language: "typescript",
			},
			{
				title: "Update send calls",
				description:
					"SendPigeon uses the same fields. The main difference is method style.",
				before: `await resend.emails.send({
  from: "hello@yourdomain.com",
  to: "user@example.com",
  subject: "Hello",
  html: "<p>Welcome!</p>",
});`,
				after: `await client.send({
  from: "hello@yourdomain.com",
  to: "user@example.com",
  subject: "Hello",
  html: "<p>Welcome!</p>",
});`,
				language: "typescript",
			},
			{
				title: "Update webhook handlers (optional)",
				description:
					"If using webhooks, update the event types. Structure is similar.",
				before: `// Resend webhook events
type: "email.sent" | "email.delivered" | "email.bounced"`,
				after: `// SendPigeon webhook events
type: "email.sent" | "email.delivered" | "email.bounced" | "email.complained"`,
				language: "typescript",
			},
		],
	},
	{
		slug: "from-sendgrid",
		name: "SendGrid",
		from: "SendGrid",
		description: "Switch from SendGrid to SendPigeon in 5 minutes",
		metaDescription:
			"Migrate from SendGrid to SendPigeon. Step-by-step guide with code examples.",
		steps: [
			{
				title: "Update your API key",
				description: "Replace SendGrid API key with SendPigeon.",
				before: "SENDGRID_API_KEY=SG.xxx...",
				after: "SENDPIGEON_API_KEY=sk_live_abc123...",
				language: "bash",
			},
			{
				title: "Update imports",
				description: "Replace the SendGrid import with SendPigeon.",
				before: `import sgMail from "@sendgrid/mail";

sgMail.setApiKey(process.env.SENDGRID_API_KEY);`,
				after: `import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(process.env.SENDPIGEON_API_KEY);`,
				language: "typescript",
			},
			{
				title: "Update send calls",
				description: "Same simple object structure. Just swap the method name.",
				before: `await sgMail.send({
  to: "user@example.com",
  from: "hello@yourdomain.com",
  subject: "Hello",
  html: "<p>Welcome!</p>",
});`,
				after: `await client.send({
  to: "user@example.com",
  from: "hello@yourdomain.com",
  subject: "Hello",
  html: "<p>Welcome!</p>",
});`,
				language: "typescript",
			},
			{
				title: "Update templates (if using)",
				description:
					"SendPigeon uses Handlebars syntax. Rename the data field.",
				before: `// SendGrid dynamic template
html: "Hello {{name}}"
dynamic_template_data: { name: "John" }`,
				after: `// SendPigeon template
html: "Hello {{name}}"
variables: { name: "John" }`,
				language: "typescript",
			},
		],
	},
	{
		slug: "from-postmark",
		name: "Postmark",
		from: "Postmark",
		description: "Switch from Postmark to SendPigeon in 5 minutes",
		metaDescription:
			"Migrate from Postmark to SendPigeon. Step-by-step guide with code examples.",
		steps: [
			{
				title: "Update your API key",
				description: "Replace Postmark server token with SendPigeon API key.",
				before: "POSTMARK_SERVER_TOKEN=xxx...",
				after: "SENDPIGEON_API_KEY=sk_live_abc123...",
				language: "bash",
			},
			{
				title: "Update imports",
				description: "Similar client initialization pattern.",
				before: `import * as postmark from "postmark";

const client = new postmark.ServerClient(
  process.env.POSTMARK_SERVER_TOKEN
);`,
				after: `import { SendPigeon } from "sendpigeon";

const client = new SendPigeon(process.env.SENDPIGEON_API_KEY);`,
				language: "typescript",
			},
			{
				title: "Update send calls",
				description: "Nearly identical field names.",
				before: `await client.sendEmail({
  From: "hello@yourdomain.com",
  To: "user@example.com",
  Subject: "Hello",
  HtmlBody: "<p>Welcome!</p>",
});`,
				after: `await client.send({
  from: "hello@yourdomain.com",
  to: "user@example.com",
  subject: "Hello",
  html: "<p>Welcome!</p>",
});`,
				language: "typescript",
			},
		],
	},
];

export function getAllMigrations(): MigrationGuide[] {
	return migrations;
}

export function getMigrationBySlug(slug: string): MigrationGuide | undefined {
	return migrations.find((m) => m.slug === slug);
}
